import { Button } from '../ui/Button';
import type { EventWithVenue } from './types';

const formatEventType = (eventType: string): string => {
  return eventType.charAt(0).toUpperCase() + eventType.slice(1);
};

interface EventDetailHeaderProps {
  event: EventWithVenue;
  isAtCapacity: boolean;
  canManage: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onRequestDelete: () => void;
}

/**
 * Title block of the event detail page: type/audience/status badges, the event
 * name, the venue line, and the edit/delete controls for authorized users.
 */
export const EventDetailHeader = ({
  event,
  isAtCapacity,
  canManage,
  isDeleting,
  onEdit,
  onRequestDelete,
}: EventDetailHeaderProps) => (
  <div className="border-b border-gray-200 pb-6 mb-6">
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
            {formatEventType(event.eventType)}
          </span>
          {event.audience === 'members_only' && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
              Members Only
            </span>
          )}
          {event.isAllDay && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              All Day
            </span>
          )}
          {isAtCapacity && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              At Capacity
            </span>
          )}
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              event.status === 'published'
                ? 'bg-green-100 text-green-800'
                : event.status === 'draft'
                  ? 'bg-gray-100 text-gray-800'
                  : event.status === 'cancelled'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-primary-100 text-primary-800'
            }`}
          >
            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
          </span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">{event.name}</h1>
        {event.venue?.name && (
          <p className="text-lg text-gray-600 mt-2">{event.venue.name}</p>
        )}
      </div>

      {/* Edit/Delete buttons for authorized users */}
      {canManage && (
        <div className="flex gap-2 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
          >
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-red-600 text-red-600 hover:bg-red-50"
            onClick={onRequestDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      )}
    </div>
  </div>
);
